import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  TextField,
  MenuItem,
  Button,
  Avatar,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PersonIcon from "@mui/icons-material/Person";
import en_ja from "../../translations/en_ja";
import CustomTable from "../../ReusableComponents/Table/CustomTable";
import api from "../../api/api";
import { DownloadCSV } from "../../ReusableComponents/DownloadCSV";
import DownloadIcon from '@mui/icons-material/Download';

const BmiStatus = ({ dialogOpen, onClose, filteredData = null }) => {
  const [loading, setLoading] = useState(true);
  const [bmiUsers, setBmiUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [filters, setFilters] = useState({
    bmi: "",
    gender: "",
    age: "",
  });

  const mapUsers = (users = []) =>
    users.map((item) => ({
      id: item.user_id,
      name: `${item.user.first_name || "-"} ${item.user.last_name || ""}`,
      email: item.user.email || "-",
      contact: item.user.phone_number || "-",
      gender: Number(item.user.gender),
      bmi: item.bmi || "-",
      age: item.age_answer || "-",
      profile_image: item.user.profile_image,
    }));

  useEffect(() => {
    if (!dialogOpen) return;

    if (filteredData) {
      const users = mapUsers(filteredData.users || []);
      setBmiUsers(users);
      setFilteredUsers(users);
      setFilters({ bmi: "", gender: "", age: "" });
      setLoading(false);
    } else {
      getAllUserDetails();
    }
  }, [filteredData, dialogOpen]);

  const getAllUserDetails = async () => {
    setLoading(true);
    try {
      const res = await api.get("/v1/admin/user/bmi-stats");
      if (res?.data?.data?.users) {
        const users = mapUsers(res.data.data.users);
        setBmiUsers(users);
        setFilteredUsers(users);
      }
    } catch (err) {
      console.error("Error fetching BMI users:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = async (e) => {
    const { name, value } = e.target;

    // If BMI filter changes — reset gender & age filters
    if (name === "bmi") {
      setFilters({ bmi: value, gender: "", age: "" });

      if (value) {
        setLoading(true);
        try {
          // keep the Reports date range (if any) so BMI + date filter combine
          const start = filteredData?.range?.start;
          const end = filteredData?.range?.end;
          let url = `/v1/admin/user/bmi-stats?bmi=${value}`;
          if (start && end) {
            url += `&startDate=${String(start).slice(0, 10)}&endDate=${String(end).slice(0, 10)}`;
          }

          const res = await api.get(url);
          if (res?.data?.data?.users) {
            const users = mapUsers(res.data.data.users);
            setBmiUsers(users);
            setFilteredUsers(users);
          }
        } catch (err) {
          console.error("Error fetching BMI filtered data:", err);
        } finally {
          setLoading(false);
        }
      } else {
        // BMI cleared: restore the date-filtered set if present, else all users
        if (filteredData) {
          const users = mapUsers(filteredData.users || []);
          setBmiUsers(users);
          setFilteredUsers(users);
        } else {
          getAllUserDetails();
        }
      }
      return;
    }

    // For other filters (age, gender)
    const updatedFilters = { ...filters, [name]: value };
    setFilters(updatedFilters);

    let filtered = [...bmiUsers];

    // Gender filter
    if (updatedFilters.gender) {
      filtered = filtered.filter(
        (user) =>
          (updatedFilters.gender === "Male" && user.gender === 0) ||
          (updatedFilters.gender === "Female" && user.gender === 1)
      );
    }

    // Age filter
    if (updatedFilters.age) {
      if (updatedFilters.age === "Above 18") {
        filtered = filtered.filter((user) => Number(user.age) > 18);
      } else if (updatedFilters.age === "Above 25") {
        filtered = filtered.filter((user) => Number(user.age) > 25);
      } else if (updatedFilters.age === "Above 40") {
        filtered = filtered.filter((user) => Number(user.age) > 40);
      }
    }

    setFilteredUsers(filtered);
  };

  const resetFilters = () => {
    setFilters({ bmi: "", gender: "", age: "" });
    // the BMI filter re-fetches from the server and overwrites bmiUsers,
    // so restore the original base dataset instead of the filtered one.
    if (filteredData) {
      const users = mapUsers(filteredData.users || []);
      setBmiUsers(users);
      setFilteredUsers(users);
    } else {
      getAllUserDetails();
    }
  };

  const columns = [
    {
      id: "name",
      label: en_ja["Name"] || "Name",
      sortable: true,
      render: (row) => (
        <Box display="flex" alignItems="center" gap={1.5}>
          <Avatar
            src={row.profile_image || ""}
            sx={{ bgcolor: "primary.light", width: 40, height: 40 }}
          >
            {!row.profile_image && <PersonIcon />}
          </Avatar>
          <Typography noWrap>{row.name}</Typography>
        </Box>
      ),
    },
    { id: "email", label: en_ja["Email"] || "Email", sortable: true },
    {
      id: "contact",
      label: en_ja["Contact"] || "Contact",
      render: (row) => <Typography noWrap>{row.contact || "-"}</Typography>,
      sortable: true,
    },
    { id: "age", label: en_ja["Age"] || "Age", sortable: true },
    {
      id: "gender",
      label: en_ja["Gender"] || "Gender",
      render: (row) => (
        <Typography noWrap>
          {row.gender === 0
            ? en_ja["Male"]
            : row.gender === 1
              ? en_ja["Female"]
              : "-"}
        </Typography>
      ),
      sortable: true,
    },
    { id: "bmi", label: en_ja["BMI"] || "BMI", sortable: true },
  ];

  return (
    <Dialog open={dialogOpen} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" fontWeight="600">
            {en_ja["High BMI Status"] || "High BMI Status"}
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ py: 1 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          {/* Left side - Download button */}
          <Button
            variant="contained"
            color="primary"
            startIcon={<DownloadIcon />}
            sx={{ textTransform: "none", borderRadius: "8px" }}
            onClick={() => {
              const csvColumns = [
                { id: "name", label: en_ja["Name"] || "Name" },
                { id: "email", label: en_ja["Email"] || "Email" },
                { id: "contact", label: en_ja["Contact"] || "Contact" },
                { id: "age", label: en_ja["Age"] || "Age" },
                {
                  id: "gender",
                  label: en_ja["Gender"] || "Gender",
                  render: (row) =>
                    row.gender === 0
                      ? en_ja["Male"] || "Male"
                      : row.gender === 1
                        ? en_ja["Female"] || "Female"
                        : "-",
                },
                { id: "bmi", label: en_ja["BMI"] || "BMI" },
              ];
              DownloadCSV(csvColumns, filteredUsers, en_ja["BMI_Status_Report"]);
            }}
          >
            {en_ja["BMI Status"] || "BMI Status"}
          </Button>

          {/* Right side - Filters */}
          <Box display="flex" alignItems="center" gap={2}>
            <TextField
              select
              name="bmi"
              value={filters.bmi}
              onChange={handleFilterChange}
              size="small"
              sx={{ minWidth: 150 }}
              label={en_ja["Select BMI"] || "Select BMI"}
            >
              <MenuItem value="">{en_ja["Select BMI"] || "Select BMI"}</MenuItem>
              <MenuItem value="25">{en_ja["Above 25"] || "Above 25"}</MenuItem>
              <MenuItem value="30">{en_ja["Above 30"] || "Above 30"}</MenuItem>
              <MenuItem value="35">{en_ja["Above 35"] || "Above 35"}</MenuItem>
              <MenuItem value="40">{en_ja["Above 40"] || "Above 40"}</MenuItem>
            </TextField>

            <TextField
              select
              name="gender"
              value={filters.gender}
              onChange={handleFilterChange}
              size="small"
              sx={{ minWidth: 150 }}
              label={en_ja["Gender"] || "Gender"}
            >
              <MenuItem value="">{en_ja["Gender"] || "Gender"}</MenuItem>
              <MenuItem value="Male">{en_ja["Male"] || "Male"}</MenuItem>
              <MenuItem value="Female">{en_ja["Female"] || "Female"}</MenuItem>
            </TextField>

            <TextField
              select
              name="age"
              value={filters.age}
              onChange={handleFilterChange}
              size="small"
              sx={{ minWidth: 150 }}
              label={en_ja["Select Age"] || "Select Age"}
            >
              <MenuItem value="">{en_ja["Select Age"] || "Select Age"}</MenuItem>
              <MenuItem value="Above 18">{en_ja["Above 18"] || "Above 18"}</MenuItem>
              <MenuItem value="Above 25">{en_ja["Above 25"] || "Above 25"}</MenuItem>
              <MenuItem value="Above 40">{en_ja["Above 40"] || "Above 40"}</MenuItem>
            </TextField>

            <Button
              onClick={resetFilters}
              sx={{
                textTransform: "none",
                bgcolor: "#f5f5f5",
                borderRadius: "10px",
                color: "#333",
                "&:hover": { bgcolor: "#e0e0e0" },
              }}
            >
              {en_ja["Reset Filters"] || "Reset Filters"}
            </Button>
          </Box>
        </Box>

        {loading ? (
          <Typography textAlign="center" py={3}>
            {en_ja["Loading..."] || "Loading..."}
          </Typography>
        ) : filteredUsers.length === 0 ? (
          <Typography textAlign="center" py={3} color="text.secondary">
            {en_ja["No data found"] || "No data found"}
          </Typography>
        ) : (
          <CustomTable
            columns={columns}
            rows={filteredUsers}
            loading={loading}
            showIndexColumn={true}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default BmiStatus;
